import {
  AfterContentInit,
  AfterViewInit,
  Component,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { switchMap } from 'rxjs/operators';
import {ActivatedRoute, NavigationStart, Router} from '@angular/router';
import {Registration} from '../../models/registration';
import {
  ExtendedSearchReqResponse, PatternSearchResponse,
  SearchReqResponse,
  SearchService,
} from 'src/app/services/search-service';
import {RegistrationService} from 'src/app/services/registration-service';
import {SynonymResult} from 'src/app/models/synonymResult';
import {BYOTermResponse} from 'src/app/models/byoTermResponse';
import {UserService} from 'src/app/services/user-service';
import {LoggerService} from 'src/app/services/logger-service';
import {AltSearchOption} from 'src/app/models/altSearchOption';
import {QuickSignupService} from 'src/app/services/quick-signup-service';
import { AccountV2Service } from 'src/app/services/account-v2-service';
import {SessionService} from 'src/app/services/session-service';
import {
  TrackingService,
  TRACKING_SEARCH_TYPE,
} from 'src/app/services/tracking-service';
import {FeatureType} from 'src/app/components/featured-plates/featured-plates.component';
import {PlatexUserAlert} from 'src/app/models/platexUserAlert';
import {PAGE_PLACEMENTS, PagePlacement} from 'src/app/components/page-placement/page-placement.component';
import {
  SidebarFilter,
  SidebarFilterConfig,
  SidebarFilterGroup
} from 'src/app/components/side-bar-filters/side-bar-filters.component';
import {DropdownConfig, DropdownOption} from 'src/app/components/custom-dropdown/custom-dropdown.component';
import {environment} from 'src/environments/environment';
import {StickyConfig, StickyLink, StickyLinkGroup} from 'src/app/components/sticky-tabs/sticky-tabs.component';
import {ViewportScroller} from '@angular/common';
import {LiveV2ExactMatch, LiveV2SearchResponse, LiveV2SearchResultItem} from 'src/app/models/liveV2Search';
import {RankDifference, ShadowLogEntry} from 'src/app/models/shadowLog';

/** Simple RFC-4122 v4 UUID without a dependency on the uuid package. */
function uuidv4(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
    const r = Math.random() * 16 | 0;
    return (c === 'x' ? r : (r & 0x3 | 0x8)).toString(16);
  });
}
import {MatDialog} from '@angular/material/dialog';
import {FeedbackDialogModalComponent} from 'src/app/components/feedback-dialog-modal/feedback-dialog-modal.component';
import { accountV2Enabled as accountV2Site } from 'src/app/site-config';

export interface SearchFilters {
  sortBy: string,
  up_to_350: boolean;
  from_350_to_750: boolean;
  over_750: boolean;
  new_issue: boolean;
  new_release: boolean;
  resale: boolean;
  auction: boolean;
  private_listing: boolean;
  current: boolean;
  prefix: boolean;
  suffix: boolean;
  dateless: boolean;
}

@Component({
  selector: 'pla-search-results-page',
  templateUrl: './search-results-page.component.html',
  styleUrls: ['./search-results-page.component.scss'],
})
export class SearchResultsPageComponent implements OnInit, AfterViewInit {
  public sortTrigger: EventEmitter<void> = new EventEmitter<void>();
  public searchTrigger: EventEmitter<void> = new EventEmitter<void>();
  public mobileFilterToggle: EventEmitter<boolean> = new EventEmitter<boolean>();
  public newlyListedResults: SearchReqResponse;
  public npFeatured = new FeatureType(
    'Recently Added',
    'recently-added'
  );

  public currentFilters: any = {};
  public filterChange: EventEmitter<SearchFilters> = new EventEmitter<SearchFilters>();
  public sortDropdownConfig: DropdownConfig = new DropdownConfig('Sort By', 'swap_vert', [
    new DropdownOption('most-relevant', 'Most Relevant'),
    new DropdownOption('price-ascending', 'Price Low to High'),
    new DropdownOption('price-descending', 'Price High to Low')
  ]);
  public filterUpdate: EventEmitter<SidebarFilterConfig<any>> =
    new EventEmitter<SidebarFilterConfig<any>>();
  public searchFilters: SidebarFilterConfig<SearchFilters> =
    new SidebarFilterConfig<SearchFilters>(
      [
        // new SidebarFilterGroup('Vehicle Suitability', [
        //   new SidebarFilter('select', '', 'vehicleSuitability', {
        //     selectConfig: new DropdownConfig('Age Check', 'filter_alt', [
        //       new DropdownOption('age_check', 'Age Check')
        //     ])
        //   })
        // ]),
        new SidebarFilterGroup('Sort By', [
          new SidebarFilter('sort', 'Sort By', 'sortBy', {
            selectConfig: this.sortDropdownConfig
          })
        ], {visible: true, customClass: 'd-block d-lg-none'}),
        new SidebarFilterGroup('Price', [
          new SidebarFilter('checkbox', 'Up To £350', 'up_to_350'),
          new SidebarFilter('checkbox', '£350 to £750', 'from_350_to_750'),
          new SidebarFilter('checkbox', 'Over £750', 'over_750'),
        ]),
        new SidebarFilterGroup('Sale Type', [
          new SidebarFilter('checkbox', 'New Issue', 'new_issue'),
          new SidebarFilter('checkbox', 'Resale', 'resale'),
          // new SidebarFilter('checkbox', 'Auction', 'auction'),
          // new SidebarFilter('checkbox', 'Private Listing', 'private_listing'),
        ]),
        new SidebarFilterGroup('Plate Type', [
          new SidebarFilter('checkbox', 'Current', 'current'),
          new SidebarFilter('checkbox', 'Prefix', 'prefix'),
          new SidebarFilter('checkbox', 'Suffix', 'suffix'),
          new SidebarFilter('checkbox', 'Dateless', 'dateless'),
        ]),
      ],
      this.filterChange
    );
  public ownSimilarPlacement: PagePlacement = PAGE_PLACEMENTS.OWN_SIMILAR_FREE_3_MONTHS();
  public howDidWeDo: PagePlacement = PAGE_PLACEMENTS.HOW_DID_WE_DO();

  public pending_merge: boolean = false;
  public combined_main_results: SearchReqResponse;
  public combined_results_change: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  private searchStartTime: number;
  public superSearchAttempt = 0;
  // Plato (search_v2) can run for minutes on some wildcard terms (HU??GH). The
  // loader waits for it at most PLATO_WAIT_MS, then shows the other legs'
  // results; a late Plato answer still merges in, but is never retried.
  private static readonly PLATO_WAIT_MS = 20000;
  private platoGaveUp = false;
  public results: SearchReqResponse;
  public v3_results_map: { thirdParty: boolean, result: SearchReqResponse }[] = [];
  public v3_results: SearchReqResponse;
  public perfectResults: SearchReqResponse;
  public wildcardResults: SearchReqResponse;
  public synonymResults: SynonymResult[];
  public patternWildcard: string;
  public patternResults: SearchReqResponse[];
  public perfectSearching: boolean;
  public superSearching: boolean;
  public v3Searching: boolean;
  public superSearchComplete: EventEmitter<void> = new EventEmitter<void>();
  public wildcardSearching: boolean;
  public byoTerm: BYOTermResponse;
  public alternativeOptions: AltSearchOption[];
  private allChips: StickyLink[] = [
    new StickyLink('Close Matches', 'close-matches', true),
    new StickyLink('Similar', 'similar', false),
    new StickyLink('Current Lists', 'current-lists', false),
    new StickyLink('Prefix Lists', 'prefix-lists', false),
    // new StickyLink('Q&As', 'faq', false),
  ]
  public stickyLinkConfig: StickyConfig = new StickyConfig(
    [new StickyLinkGroup(this.allChips)],
    new EventEmitter<void>()
  );

  public searchCriteria: string;
  public routeFrom: string;
  // The redesigned (Search 12) results body serves every host since the
  // 2026-11-03 release; /search-prod-style (dev only) still forces the old body.
  public isDev2: boolean = true;
  // DEV-only: when the /search-prod-style comparison route is used we force the
  // legacy (production) results body even on dev2, while still using DEV2 data.
  public prodStyleCompare = false;
  // Render the new dev2 body only when on dev2 AND not in prod-style compare mode.
  public get useDev2Body(): boolean {
    return this.isDev2 && !this.prodStyleCompare;
  }
  // Flattened datasets for the dev2 Search 12 body's prod-style middle tabs.
  // Memoised on the source array: a fresh array on every change-detection tick
  // re-fires the body's ngOnChanges, which rebuilds every section and makes the
  // lower rows flash. The sources are always reassigned, never mutated.
  private _dev2Memo: { [key: string]: { src: any; val: any } } = {};
  private dev2Memo<T>(key: string, src: any, build: () => T): T {
    const m = this._dev2Memo[key];
    if (m && m.src === src) return m.val;
    const val = build();
    this._dev2Memo[key] = { src, val };
    return val;
  }
  /** Stable empty input, so a missing list is not a new [] every tick. */
  public readonly emptyRegs: Registration[] = [];
  public get dev2Similar(): any[] {
    return this.dev2Memo('similar', this.synonymResults, () =>
      (this.synonymResults || []).reduce(
        (acc, s) => acc.concat((s && s.resp && s.resp.registrations) || []),
        [] as any[]
      )
    );
  }
  public get dev2Current(): any[] {
    return this.dev2Memo('current', this.currentResults, () =>
      (this.currentResults || []).reduce(
        (acc, c) => acc.concat((c && c.registrations) || []),
        [] as any[]
      )
    );
  }
  public get dev2Prefix(): any[] {
    return this.dev2Memo('prefix', this.prefixResults, () =>
      (this.prefixResults || []).reduce(
        (acc, p) => acc.concat((p && p.registrations) || []),
        [] as any[]
      )
    );
  }
  // Grouped (un-flattened) wildcard pattern sets for the dev2 Search 12 body's
  // stacked Current / Prefix sections. Each entry keeps its wildcard version
  // label (e.g. 'HU??GH?') so the body can render + paginate each group on its
  // own, instead of collapsing the whole extended-search set into one flat list.
  public get dev2CurrentGroups(): { label: string; regs: any[]; count?: number }[] {
    return this.dev2Memo('currentGroups', this.currentResults, () =>
      (this.currentResults || [])
        .filter((c) => c && c.registrations && c.registrations.length > 0)
        .map((c) => ({ label: c.id, regs: c.registrations, count: (c as any).count }))
    );
  }
  public get dev2PrefixGroups(): { label: string; regs: any[]; count?: number }[] {
    return this.dev2Memo('prefixGroups', this.prefixResults, () =>
      (this.prefixResults || [])
        .filter((p) => p && p.registrations && p.registrations.length > 0)
        .map((p) => ({ label: p.id, regs: p.registrations, count: (p as any).count }))
    );
  }
  // Re-searches from this page must stay on the same route so the comparison
  // view keeps the production UX instead of bouncing to the new dev2 results.
  public get searchRoutePrefix(): string {
    return this.prodStyleCompare ? 'search-prod-style/' : 'search-results/';
  }
  public newlyListedResultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();
  public resultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();
  public v3_resultChange: EventEmitter<SearchReqResponse> = new EventEmitter<SearchReqResponse>();
  public perfectResultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  public currentResults: SearchReqResponse[];
  public prefixResults: SearchReqResponse[];

  public wildcardResultChange: EventEmitter<SearchReqResponse> =
    new EventEmitter<SearchReqResponse>();

  public bannerRegistration: string = 'S34 RCH';
  public bannerUpdate: EventEmitter<string> = new EventEmitter<string>();
  public fetchedAlerts: boolean = false;
  public alertActionLoading: boolean = false;
  public shownPopup: boolean = false;

  // --- Mode 2 (live_v2) primary search state ---
  // v2Enabled mirrors the client flag. v2Active is true once Mode 2 has actually served this search.
  public v2Enabled: boolean = environment.useLiveV2;
  public v2Active: boolean = false;
  public v2ExactMatch: LiveV2ExactMatch = null;

  // --- Shadow comparison state ---
  // Populated when fireShadowLegacy() completes and POSTs the comparison record.
  // Used to PATCH the record with click data when the user navigates to a plate.
  private _shadowLogId: string | null = null;
  private _shadowMode2Top50: string[] = [];
  private _shadowLegacyTop50: string[] = [];

  constructor(
    private searchService: SearchService,
    private userService: UserService,
    private registrationService: RegistrationService,
    private route: ActivatedRoute,
    private loggerService: LoggerService,
    private router: Router,
    public sessionService: SessionService,
    private quickSignupService: QuickSignupService,
    private trackingService: TrackingService,
    private viewportScroller: ViewportScroller,
    private accountV2Service: AccountV2Service,
    private dialog: MatDialog
  ) {
    this.searchCriteria =
      this.route.snapshot.params.searchCriteria ||
      this.route.snapshot.queryParams['q'];
    // DEV-only comparison route (/search-prod-style): render the production
    // results UX with DEV2 search data. Set via route data; defaults false so
    // the normal /search-results route is unaffected.
    this.prodStyleCompare = this.route.snapshot.data['prodStyleCompare'] === true;
    this.routeFrom = this.route.snapshot.queryParams['routeFrom'];
    this.trackingService.TrackSearch(
      TRACKING_SEARCH_TYPE.MAIN,
      this.searchCriteria
    );
    this.filterChange.subscribe((_: SearchFilters) => this.currentFilters = _);
  }

  ngOnInit(): void {
    // Saved searches belong to a signed-in user; signed out there is nothing to load.
    if (this.sessionService.isLoggedIn()) {
      this.userService.getPlatexUserAlerts(() => {
        this.fetchedAlerts = true;
      });
    } else {
      this.fetchedAlerts = true;
    }

    // Shadow click tracking — when the user navigates to a plate-detail page
    // while Mode 2 shadow is active, patch the shadow log with click data.
    // Uses NavigationStart so we capture the click before the component is
    // destroyed. Safe to call notifyShadowClick multiple times — it no-ops
    // after the first click (clears _shadowLogId).
    if (this.v2Enabled && environment.liveV2CompareEnabled) {
      this.router.events.subscribe(event => {
        if (event instanceof NavigationStart) {
          // Canonical /plate/{REG} and legacy /plate-detail/{REG} both count as
          // a plate click. (-detail)? keeps the optional legacy segment.
          const match = event.url.match(/\/plate(?:-detail)?\/([^/?]+)/);
          if (match) {
            this.notifyShadowClick(match[1]);
          }
        }
      });
    }

    if (this.superSearching || this.perfectSearching || this.wildcardSearching) {
      return;
    }

    this.search();
    // this.searchAltTerms();
  }

  ngAfterViewInit(): void {
    window.scrollTo(0, 1);
    setTimeout(() => {
      window.scrollTo(0, 1);
    }, 100);
  }

  public SortChange(e: DropdownOption, key: string): void {
    this.currentFilters[key] = e.value;
    this.filterChange.emit(this.currentFilters);
  }

  public generateSynonymHeaderText(synonymResult: SynonymResult): string {
    return `Similar search results to ${this.searchCriteria.toUpperCase()}`;
    return `Alternative search results for ${this.searchCriteria.toUpperCase()} - "${synonymResult.synonym.toUpperCase()}"`;
  }

  public routeToNewSearch(term: AltSearchOption): void {
    this.router.navigate([`/${this.searchRoutePrefix}${term.term}`], {
      queryParams: {routeFrom: this.searchCriteria.toUpperCase()},
    });
  }

  // Single page-level feedback entry point. Replaces the per-row "Seen an error?"
  // links removed in this PR — same modal, same /feedback endpoint, same Jira
  // pipeline; only the surfacing changes.
  public reportPageIssue(): void {
    this.dialog.open(FeedbackDialogModalComponent, {
      width: '420px',
      maxWidth: '92vw',
      data: {
        context: {
          source: 'search-results-page',
          term: this.searchCriteria,
        },
      },
    });
  }

  public hasAlert(): boolean {
    return this.userService.hasAlert(this.searchCriteria);
  }

  public addQuickAlert(): void {
    if (this.alertActionLoading) return;
    if (!this.searchCriteria || this.searchCriteria == '') return;
    this.alertActionLoading = true;
    var alert = new PlatexUserAlert();
    alert.term = this.searchCriteria;
    alert.registrationStyle = 'all_styles';
    this.userService.addAlert(alert, (_alerts: PlatexUserAlert[]) => {
      this.alertActionLoading = false;
    }, () => {
      this.alertActionLoading = false;
    });
  }

  public cancelQuickAlert(): void {
    if (this.alertActionLoading) return;
    if (!this.searchCriteria || this.searchCriteria == '') return;
    this.alertActionLoading = true;
    var alert = new PlatexUserAlert();
    alert.term = this.searchCriteria;
    this.userService.removeAlert(alert, (_alerts: PlatexUserAlert[]) => {
      this.alertActionLoading = false;
    }, () => {
      this.alertActionLoading = false;
    });
  }

  // ----- Account v2: Save this search (dev-only route swap, like the shell).
  // Creates a saved search with the s3 defaults (new plates, daily digest);
  // everything is adjustable from /account/searches afterwards.
  public accountV2Enabled: boolean = accountV2Site;
  public savedSearchState: 'idle' | 'saving' | 'saved' = 'idle';

  public saveThisSearch(): void {
    if (this.savedSearchState !== 'idle') return;
    const term = (this.searchCriteria || '').trim();
    if (term === '') return;
    this.quickSignupService.quickSignup(
      `save search ${term.toUpperCase()}`,
      undefined,
      (ok: boolean) => {
        if (ok) this.commitSaveSearch(term);
      },
      () => { }
    );
  }

  private commitSaveSearch(term: string): void {
    this.savedSearchState = 'saving';
    // Daily where the alert system offers it, otherwise its own default
    // (production's legacy alerts are instant only).
    this.accountV2Service.getCapabilities().pipe(
      switchMap((caps) => this.accountV2Service.createSearch({
        name: `${term.toUpperCase()} plates`,
        criteria: JSON.stringify({ term: term.toUpperCase() }),
        plainSummary: `Registrations matching "${term.toUpperCase()}"`,
        alertScope: ['new'],
        frequency: caps.searchFrequencies.includes('daily') ? 'daily' : caps.searchFrequencies[0],
      }))
    ).subscribe({
      next: () => (this.savedSearchState = 'saved'),
      // 409: already saved for this word, which is what the member wanted
      error: (e) => (this.savedSearchState = e && e.status === 409 ? 'saved' : 'idle'),
    });
  }

  public search() {
    this.searchStartTime = new Date().getTime();
    this.superSearchAttempt = 0;
    this.platoGaveUp = false;
    this.perfectSearching = true;
    this.superSearching = true;
    this.wildcardSearching = true;
    this.v3Searching = true;   // keep Close Matches in loading state until primary search returns
    this._shadowLogId = null;
    this._shadowMode2Top50 = [];
    this._shadowLegacyTop50 = [];

    // Mode 2 is the primary visible search when useLiveV2 is true.
    // Mode 2 renders results immediately; the legacy super-search runs shadow-only.
    // Falls back to the full legacy path when Mode 2 returns null (empty config, error).
    if (this.v2Enabled) {
      this.searchMode2Path();
      return;
    }
    this.searchLegacy();
  }

  // The original six-call merge path — unchanged, reachable as legacy fallback
  // and as the shadow path (called async from searchMode2Path when shadow is enabled).
  private searchLegacy(): void {
    this.userService.getUserData(() => {
      setTimeout(() => {
        this.viewportScroller.scrollToAnchor('recent-searches');
      }, 500);
      this.searchRaw(() => {
      });
      this.searchV3(true);
      this.searchV3(false);
      this.searchSynonym();
      this.searchPattern();
      this.searchPerfect(() => {
        this.searchComplete();
        // this.getBYO();
      });
      this.searchExtended();
    });
  }

  /**
   * Mode 2 primary search path — two-call architecture.
   *
   * Mirrors the original production model: calls Mode 2 Lambda twice
   * concurrently — once with thirdPartyOnly=true (dealer inventory) and
   * once with thirdPartyOnly=false (DVLA inventory). The backend's
   * _partition_by_owner() guarantees the two responses are disjoint, so
   * concatenation is naturally duplicate-free. A Set guard removes any
   * accidental overlaps defensively.
   *
   * Rendering waits for both calls to return. If one call fails or returns
   * null, the other set is rendered alone. If both fail, falls back to the
   * full legacy path.
   */
  private searchMode2Path(): void {
    const query = this.searchCriteria;
    let dealerRes: SearchReqResponse | null = null;
    let dvlaRes: SearchReqResponse | null = null;
    // Fast Plato leg: process=search_v2 against the canonical plato-search URL
    // (the optimised Mode 1 fast engine production uses). It contributes
    // ADDITIONAL registrations + dealer inventory on top of Mode 2's coverage;
    // it never blocks Mode 2's immediate first paint.
    let platoRes: SearchReqResponse | null = null;
    let dealerDone = false;
    let dvlaDone = false;
    let platoDone = false;
    let firstRendered = false;
    let mode2Finalized = false;

    // PERF: the peripheral sections depend only on the search term, not on the
    // Close-Matches result, so fire them NOW in parallel with the primary
    // search_v3 calls instead of waiting for the (sometimes slow) merge. This
    // stops Perfect / Similar / Current / Prefix being blocked behind search_v3.
    // (process=pattern is not consumed by the dev2 results body — skipped.)
    this.searchPerfect(() => { this.searchComplete(); });
    this.searchSynonym();
    this.searchExtended();

    // Build + render the Close-Matches set from whatever search_v3 partitions
    // have returned so far. Called first with one partition (fast first paint),
    // then again with both (final, deduped). The Set guard makes re-merge safe.
    const keyOf = (r: Registration): string =>
      (r.registration || '').toUpperCase().replace(/\s/g, '');
    const renderClose = (): void => {
      const dealerRegs: Registration[] = dealerRes ? (dealerRes.registrations || []) : [];
      const dvlaRegs: Registration[]   = dvlaRes   ? (dvlaRes.registrations   || []) : [];
      const seen = new Set<string>();
      const merged: Registration[] = [];
      for (const r of [...dealerRegs, ...dvlaRegs]) {
        const key = keyOf(r);
        if (key && !seen.has(key)) { seen.add(key); merged.push(r); }
      }
      // Fold in the fast Plato leg when it has returned. Shared registrations are
      // enriched in place (adopt a real dealer listing / cheaper price so no
      // seller inventory is lost); Plato-unique registrations are added. The
      // combined list is then ranked by relevance (percentage desc, stable — the
      // SAME ranking MergeResults uses), so Plato's extras are relevance-placed,
      // not dumped at the bottom. When Plato has NOT returned the Mode 2-only
      // behaviour below is byte-for-byte unchanged.
      let input: Registration[] = merged;
      const havePlato = !!(platoRes && platoRes.registrations && platoRes.registrations.length);
      if (havePlato) {
        const platoRegs = this.registrationService.formatRegistrations(
          Array.from(new Map(platoRes.registrations.map(r => [keyOf(r), r])).values())
        );
        const byKey = new Map<string, Registration>(merged.map(r => [keyOf(r), r]));
        const platoUnique: Registration[] = [];
        for (const p of platoRegs) {
          const k = keyOf(p);
          const existing = byKey.get(k);
          if (existing) this.enrichListing(existing, p);
          else { platoUnique.push(p); byKey.set(k, p); }
        }
        input = [...merged, ...platoUnique];
      }
      let formatted = this.registrationService.formatRegistrations(input);
      if (havePlato) {
        // Stable percentage-desc sort: equal relevance keeps input order, so the
        // Mode 2 rows win ties and their order is preserved; higher-relevance
        // Plato extras rise to their proper position. Reassign index to match.
        formatted = formatted
          .sort((a, b) => (b.percentage || 0) - (a.percentage || 0))
          .map((reg, i) => { reg.index = i + 1; return reg; });
      }
      const reqResp = new SearchReqResponse('mode2', formatted);
      this.results = reqResp;
      this.combined_main_results = reqResp;
      this.superSearching = false;
      this.v3Searching = false;
      this.perfectSearching = false;
      this.wildcardSearching = false;
      this.combined_results_change.emit(this.combined_main_results);
      this.superSearchComplete.emit();
      this.filterUpdate.emit(this.searchFilters);
    };

    const tryMerge = () => {
      // Both failed — fall back to the full legacy search.
      if (dealerDone && dvlaDone && dealerRes == null && dvlaRes == null) {
        this.v2Active = false;
        this.searchLegacy();
        return;
      }
      try {
        // First partition back → paint Close Matches immediately (progressive).
        // First paint is driven by Mode 2 only (its established immediate-results
        // behaviour); the Plato leg never delays it.
        if (!firstRendered && (dealerRes || dvlaRes)) {
          this.v2Active = true;
          this.v2ExactMatch = null;
          renderClose();
          firstRendered = true;
        } else if (firstRendered) {
          // A later arrival (second Mode 2 partition, or the Plato leg) → re-merge
          // seamlessly into the already-painted list.
          renderClose();
        }
        // Both Mode 2 partitions back → run the finalise side-effects once (they
        // must not depend on the Plato leg, which may still be in flight).
        if (dealerDone && dvlaDone && !mode2Finalized) {
          mode2Finalized = true;
          if (query) {
            const n = this.combined_main_results.registrations.length;
            this.userService.addSearchToHistory(query, `${n} Results`);
          }
          this.extractNewlyListedFromResults();
          if (environment.liveV2CompareEnabled) {
            const mode2Regs = this.combined_main_results.registrations.map(r => r.registration).slice(0, 50);
            this.fireShadowLegacy(query, mode2Regs);
          }
          this.searchComplete();
        }
      } catch (ex: any) {
        this.loggerService.logException(ex);
        this.v2Active = false;
        this.v2ExactMatch = null;
        this.searchLegacy();
      }
    };

    // Fire both partitions concurrently. Limit=200 matches Mode 2's MAX_RETURN.
    this.searchService.searchMode2(query, 200, (res: SearchReqResponse | null) => {
      dealerRes = res;
      dealerDone = true;
      tryMerge();
    }, true);

    this.searchService.searchMode2(query, 200, (res: SearchReqResponse | null) => {
      dvlaRes = res;
      dvlaDone = true;
      tryMerge();
    }, false);

    // Fast Plato engine (process=search_v2 via canonical plato-search URL) fired
    // concurrently. Supplementary: it adds registrations + dealer inventory and
    // enriches shared rows, but is never awaited for the initial paint. A null
    // response (error) simply leaves Mode 2's results as they are.
    this.searchService.searchRawCanonical(query, (res: SearchReqResponse | null) => {
      platoRes = res;
      platoDone = true;
      if (firstRendered) tryMerge();
    });
  }

  /**
   * Fire the legacy super-search pipeline async.
   *
   * Calls searchRaw (process=search_v2), searchV3(true), and searchV3(false)
   * concurrently using the existing plato-search (canonical) URL. When all
   * three complete, computes the diff vs Mode 2 results and POSTs a shadow
   * log entry. The shadow path MUST NOT affect rendering, loading state,
   * result display, clicks, or navigation.
   *
   * The `searchRaw`, `searchShadowV3(true)`, and `searchShadowV3(false)` calls
   * use `platoSearchCanonicalBase` which always reaches the legacy plato-search
   * Lambda, not Mode 2.
   */
  private fireShadowLegacy(query: string, mode2Top50: string[]): void {
    if (!environment.liveV2CompareEnabled) return;

    const timestamp = new Date().toISOString();
    let rawDone = false, v3TpDone = false, v3NonTpDone = false;
    let rawRegs: string[] = [], v3TpRegs: string[] = [], v3NonTpRegs: string[] = [];

    const tryComplete = () => {
      if (!rawDone || !v3TpDone || !v3NonTpDone) return;

      // Merge legacy results, dedupe, take top 50.
      const legacyAll = [...v3TpRegs, ...v3NonTpRegs, ...rawRegs];
      const legacySeen = new Set<string>();
      const legacyTop50: string[] = [];
      for (const r of legacyAll) {
        if (!legacySeen.has(r) && legacyTop50.length < 50) {
          legacySeen.add(r);
          legacyTop50.push(r);
        }
      }

      const mode2Set = new Set(mode2Top50);
      const legacySet = new Set(legacyTop50);
      const onlyInMode2 = mode2Top50.filter(r => !legacySet.has(r));
      const onlyInLegacy = legacyTop50.filter(r => !mode2Set.has(r));
      const rankDifferences: RankDifference[] = mode2Top50
        .filter(r => legacySet.has(r))
        .map(r => ({
          registration: r,
          mode2_rank: mode2Top50.indexOf(r) + 1,
          legacy_rank: legacyTop50.indexOf(r) + 1,
          delta: (mode2Top50.indexOf(r) + 1) - (legacyTop50.indexOf(r) + 1),
        }))
        .filter(d => d.delta !== 0);

      const logId = uuidv4();
      const entry: ShadowLogEntry = {
        id: logId,
        query: query.toUpperCase(),
        timestamp,
        mode2_top_50: mode2Top50,
        legacy_top_50: legacyTop50,
        only_in_mode2: onlyInMode2,
        only_in_legacy: onlyInLegacy,
        rank_differences: rankDifferences,
        clicked_registration: null,
        mode2_rank: null,
        legacy_rank: null,
      };

      // Store for click tracking, then fire-and-forget POST.
      this._shadowLogId = logId;
      this._shadowMode2Top50 = mode2Top50;
      this._shadowLegacyTop50 = legacyTop50;

      this.searchService.postShadowLog(entry).subscribe({ error: () => {} });
    };

    // searchRaw = process=search_v2 via canonical plato-search URL.
    this.searchService.searchRaw(query, (res: SearchReqResponse) => {
      rawRegs = (res?.registrations || []).map(r => r.registration).filter(Boolean);
      rawDone = true;
      tryComplete();
    });

    // searchV3 via canonical URL — these hit legacy plato-search, not Mode 2.
    this.searchService.searchV3(query, (res: SearchReqResponse) => {
      v3TpRegs = (res?.registrations || []).map(r => r.registration).filter(Boolean);
      v3TpDone = true;
      tryComplete();
    }, true);

    this.searchService.searchV3(query, (res: SearchReqResponse) => {
      v3NonTpRegs = (res?.registrations || []).map(r => r.registration).filter(Boolean);
      v3NonTpDone = true;
      tryComplete();
    }, false);
  }

  /**
   * Called when the user clicks a result plate while Mode 2 shadow is active.
   * PATCHes the shadow log entry with click data.
   * Safe to call with any registration — silently no-ops if no log is active.
   */
  public notifyShadowClick(registration: string): void {
    if (!this._shadowLogId || !registration) return;
    const reg = registration.toUpperCase().replace(/\s/g, '');
    const m2Rank = this._shadowMode2Top50.indexOf(reg);
    const lgRank = this._shadowLegacyTop50.indexOf(reg);
    this.searchService.patchShadowLog(
      this._shadowLogId,
      reg,
      m2Rank >= 0 ? m2Rank + 1 : null,
      lgRank >= 0 ? lgRank + 1 : null,
    ).subscribe({ error: () => {} });
    // Clear so subsequent clicks don't double-log.
    this._shadowLogId = null;
  }

  // Map one projection result row -> the existing Registration view-model.
  // Price rule: only LISTED rows with a real price show a price; SOLD_HISTORY / UNAVAILABLE /
  // CANONICAL map to -1 so the row renders "Not Available" (no fake price) and formatRegistrations
  // sets available=false. score preserves the server ordering (formatRegistrations sorts by score).
  private mapV2Item(item: LiveV2SearchResultItem, index: number): Registration {
    const listed = item.listingState === 'LISTED' && item.bestPriceMinor != null;
    const price = listed ? item.bestPriceMinor : -1;
    const reg = new Registration(item.plateId, item.registration, item.registration, price);
    reg.score = 1000000 - index;
    reg.percentage = Math.round(((item.similarity != null ? item.similarity : item.relevanceScore) || 0) * 100);
    reg.listingState = item.listingState;
    reg.plateId = item.plateId;
    reg.sourceId = item.sourceId == null ? undefined : item.sourceId;
    reg.available = listed;
    return reg;
  }

  // Exact-match callout action (EXISTING only). Navigates to the plate page on explicit click —
  // never an automatic redirect, so the exploratory results below stay visible.
  public viewExactPlate(): void {
    if (!this.v2ExactMatch || !this.v2ExactMatch.registration) return;
    this.router.navigate([`/plates/${this.v2ExactMatch.registration}`], {
      queryParams: { s: this.searchCriteria },
    });
  }

  private searchComplete(): void {
    if (this.perfectResults == undefined || this.results == undefined) return;
    if (this.searchCriteria) {
      var resultCount = this.perfectResults.registrations.length + this.results.registrations.length
      this.userService.addSearchToHistory(this.searchCriteria, `${resultCount} Results`);
    }

    this.filterUpdate.emit(this.searchFilters); // updates order
  }

  // private searchAltTerms(): void {
  //   return; // TODO: RE-ADD
  //   this.searchService.getAltSearchTerms(
  //     this.searchCriteria.toUpperCase(),
  //     (_: AltSearchOption[]) => {
  //       if (
  //         this.routeFrom &&
  //         _.filter((__) => __.term == this.routeFrom).length == 0
  //       )
  //         _.push(
  //           new AltSearchOption(this.routeFrom, this.routeFrom, 'original')
  //         );
  //       if (_ != null) this.alternativeOptions = _.reverse();
  //     }
  //   );
  // }

  private searchExtended(): void {

    this.stickyLinkConfig.groups[0].links.map(c => {
      if (c.anchor === 'current-lists') c.hidden = true;
      if (c.anchor === 'prefix-lists') c.hidden = true;
    });

    // PERF: fire current + prefix concurrently (prefix used to wait for current
    // to return first). c_req/p_req are null on error — degrade to empty.
    this.searchService.extendedSearch(this.searchCriteria, 'current', (c_req: ExtendedSearchReqResponse[]) => {
      var hasResults = false;
      this.currentResults = (c_req || []).map(x => {
        if (x.results.length > 0) hasResults = true;
        return {id: x.version, registrations: this.registrationService.formatRegistrations(x.results), count: x.count};
      });
      this.stickyLinkConfig.groups[0].links.map(c => {
        if (c.anchor === 'current-lists') c.hidden = !hasResults;
      })
    });

    this.searchService.extendedSearch(this.searchCriteria, 'prefix', (p_req: ExtendedSearchReqResponse[]) => {
      var hasResults = false;
      this.prefixResults = (p_req || []).map(x => {
        if (x.results.length > 0) hasResults = true;
        return {id: x.version, registrations: this.registrationService.formatRegistrations(x.results), count: x.count};
      });
      this.stickyLinkConfig.groups[0].links.map(c => {
        if (c.anchor === 'prefix-lists') c.hidden = !hasResults;
      })
    });
  }

  private searchPerfect(callback: () => void = () => {
  }): void {
    this.searchService.searchPerfect(
      this.searchCriteria,
      (res: Registration[]) => {
        try {
          this.perfectSearching = false;
          if (res != null) {
            var formattedResults =
              this.registrationService.formatRegistrations(res);
            const tempSearchReq = new SearchReqResponse(
              'perfect_id',
              formattedResults
            );
            this.perfectResultChange.emit(tempSearchReq);
            this.perfectResults = tempSearchReq;
          }
          callback();
        } catch (ex: any) {
          this.loggerService.logException(ex);
          callback();
        }
      }
    );
  }

  private searchPattern(): void {
    this.searchService.searchPattern(this.searchCriteria, (res: PatternSearchResponse) => {
      let totalResults = 0;
      if (res == null) this.patternResults = null;
      else {
        const currentLength = res.current ? res.current.length : 0;
        const prefixLength = res.prefix ? res.prefix.length : 0;
        const suffixLength = res.suffix ? res.suffix.length : 0;
        const datelessLength = res.dateless ? res.dateless.length : 0;
        totalResults = currentLength + prefixLength + suffixLength + datelessLength;
      }
      if (totalResults <= 0) {
        this.patternResults = null
      } else {
        this.patternWildcard = res.wildcard || this.searchCriteria;
        this.patternResults = [
          new SearchReqResponse('Current Pattern', this.registrationService.formatRegistrations(res.current)),
          new SearchReqResponse('Prefix Pattern', this.registrationService.formatRegistrations(res.prefix)),
          new SearchReqResponse('Suffix Pattern', this.registrationService.formatRegistrations(res.suffix)),
          new SearchReqResponse('Dateless Pattern', this.registrationService.formatRegistrations(res.dateless))
        ]
      }
    });
  }

  private getBYO(callback: () => void = () => {
  }): void {
    this.searchService.getBYOTerm(
      this.searchCriteria,
      (res: BYOTermResponse) => {
        try {
          this.byoTerm = res;
          callback();
        } catch (ex: any) {
          this.loggerService.logException(ex);
          callback();
        }
      }
    );
  }

  private searchSynonym(callback: () => void = () => {
  }): void {

    this.stickyLinkConfig.groups[0].links.map(c => {
      if (c.anchor === 'similar') c.hidden = true;
    });

    this.searchService.searchSynonym(
      this.searchCriteria,
      (res: SynonymResult[]) => {
        try {
          if (res != null) {
            res.map((_res) => {
              _res.resp.id = 'synonym_id';
              _res.resp.registrations =
                this.registrationService.formatRegistrations(
                  _res.resp.registrations
                );
            });
            if (res.length == 0) return;

            const allResults = res.flatMap((r) => r.resp.registrations);
            allResults.sort((a, b) => b.percentage - a.percentage);
            const x = new SynonymResult();
            x.synonym = this.searchCriteria;
            x.resp = new SearchReqResponse(res[0].resp.id, allResults);

            this.synonymResults = [x];
            this.stickyLinkConfig.groups[0].links.map(c => {
              if (c.anchor === 'similar') c.hidden = false;
            });
          }
          callback();
        } catch (ex: any) {
          this.loggerService.logException(ex);
          callback();
        }
      }
    );
  }

  private searchV3(thirdParty: boolean): void {
    this.v3Searching = true;
    this.searchService.searchV3(
      this.searchCriteria,
      (res: SearchReqResponse) => {
        try {
          if (res != null && res.registrations.length > 0) {

            res.registrations = Array.from(
              new Map(res.registrations.map(item => [item.registration, item])).values()
            );

            res.registrations = this.registrationService.formatRegistrations(
              res.registrations
            );
          }
          this.formatNewSearchResults(res, thirdParty);
        } catch (ex: any) {
          this.formatNewSearchResults(null, thirdParty);
          this.loggerService.logException(ex);
        }
      },
      thirdParty
    );
  }

  private formatNewSearchResults(results: SearchReqResponse, thirdParty: boolean): void {
    if (results == null) {
      results = new SearchReqResponse('_____', []);
    }
    const existingIndex = this.v3_results_map.findIndex(r => r.thirdParty === thirdParty);
    if (existingIndex >= 0)
      this.v3_results_map[existingIndex].result = results;
    else
      this.v3_results_map.push({thirdParty, result: results});

    // Progressive merge: fold THIS Mode 2 (search_v3) leg into the displayed list as soon as it
    // arrives. We do not wait for the other Mode 2 leg (the slow third-party/dealer feed must
    // never delay the initial display) nor for Plato. Mode 2 does not own the ranking, so it only
    // fills gaps and enriches listings; when Plato (search_v2) arrives its scores take precedence.
    this.upsertRegistrations(results.registrations, false);

    if (this.v3_results_map.length == 2) {
      this.v3_results = new SearchReqResponse(results.id, [...this.v3_results_map[0].result.registrations, ...this.v3_results_map[1].result.registrations]);
      this.v3Searching = false;
      this.v3_resultChange.emit(this.v3_results);
      this.extractNewlyListedFromResults();
      this.superSearchComplete.emit();
      this.searchComplete();
    }
  }

  private searchRaw(callback: () => void = () => {
  }): void {
    this.superSearchAttempt++;
    const term = this.searchCriteria;
    let answered = false;
    const wait = setTimeout(() => {
      if (answered || term !== this.searchCriteria || !this.superSearching) return;
      this.platoGaveUp = true;
      this.showWithoutPlato();
      callback();
    }, SearchResultsPageComponent.PLATO_WAIT_MS);
    this.searchService.searchRaw(
      term,
      (res: SearchReqResponse) => {
        answered = true;
        clearTimeout(wait);
        // A newer search has started: this answer belongs to the old term.
        if (term !== this.searchCriteria) return;
        try {
          this.superSearching = false;

          res.registrations = Array.from(
            new Map(res.registrations.map(item => [item.registration, item])).values()
          );

          res.registrations = this.registrationService.formatRegistrations(
            res.registrations
          );

          this.results = res;

          // Plato (search_v2) owns the relevance ranking. Fold it into the displayed list
          // immediately, in score order. If a Mode 2 leg already seeded the list, Plato's scores
          // now take precedence and the list re-ranks progressively; no registration is dropped
          // and the slow dealer leg still merges in later without blocking this.
          this.upsertRegistrations(this.results.registrations, true);
          this.extractNewlyListedFromResults();
          this.superSearchComplete.emit();
          this.searchComplete();
          callback();
        } catch (ex: any) {
          this.loggerService.logException(ex);
          if (this.platoGaveUp) return; // already showing the other legs; don't rerun a slow search
          if (this.superSearchAttempt >= 3) {
            // Plato unavailable after retries: show whatever Mode 2 produced so the user still
            // gets results rather than an endless loader.
            this.showWithoutPlato();
            callback(); return;
          }
          setTimeout(() => {
            this.searchRaw(callback)
          }, 5000);
        }
      }
    );
  }

  /** Stop waiting for Plato: show what the other search legs found. */
  private showWithoutPlato(): void {
    this.superSearching = false;
    if (this.combined_main_results == null && this.v3_results != null) {
      this.upsertRegistrations(this.v3_results.registrations, false);
    }
    this.searchComplete();
  }

  /**
   * Merge a Plato (search_v2) registration's associated data into an existing
   * (Mode 2) registration that shares the same plate, WITHOUT changing its
   * ranking. Ensures dealer/seller listings and prices are never lost when both
   * engines return the same plate: adopt the incoming listing when the existing
   * row has no real listing, or when the incoming is a cheaper real listing;
   * fill individually-missing scalar fields. Mode 2 stays authoritative for
   * relevance (percentage / index are left untouched here).
   */
  private enrichListing(existing: Registration, incoming: Registration): void {
    if (existing == null || incoming == null) return;
    const real = (r: Registration): boolean =>
      r != null && r.seller != null && r.seller !== 'DVLA' &&
      typeof r.price === 'number' && r.price > 0;
    const exReal = real(existing);
    const inReal = real(incoming);
    if (inReal && (!exReal || incoming.price < existing.price)) {
      existing.seller = incoming.seller;
      existing.price = incoming.price;
      existing.available = incoming.price >= 0;
      if ((incoming as any).listingState != null) (existing as any).listingState = (incoming as any).listingState;
      if ((incoming as any).priceBreakdown != null) (existing as any).priceBreakdown = (incoming as any).priceBreakdown;
      if ((incoming as any).plateId != null) (existing as any).plateId = (incoming as any).plateId;
    }
    if ((existing.stars == null || existing.stars === 0) && incoming.stars != null) {
      existing.stars = incoming.stars;
    }
  }


  // Fold `incoming` into the single displayed list, in relevance (score) order, preserving row
  // identity. The first source to arrive seeds the list (immediate initial results, no wait for
  // Plato); later sources insert new registrations at their ranked position and data-merge
  // duplicates (see mergeRegistration) so dealer/seller listings and prices are never lost.
  // `authoritative` marks the source that owns the ranking (Plato): only it may overwrite an
  // existing row's score. The list is re-sorted by score on every call; Array.sort is stable, so
  // equal-scoring rows keep their order and the reshuffle stays minimal. The downstream table
  // ranks by `index`, so we reassign it to match the score order here.
  private upsertRegistrations(incoming: Registration[], authoritative: boolean): void {
    if (this.combined_main_results == null) {
      if (!incoming || incoming.length === 0) return;
      this.combined_main_results = new SearchReqResponse('combined', [...incoming]);
    } else if (incoming && incoming.length > 0) {
      const byReg = new Map(
        this.combined_main_results.registrations.map(r => [r.registration, r])
      );
      for (const inc of incoming) {
        if (!inc) continue;
        const existing = byReg.get(inc.registration);
        if (existing) {
          this.mergeRegistration(existing, inc, authoritative);
        } else {
          this.combined_main_results.registrations.push(inc);
          byReg.set(inc.registration, inc);
        }
      }
    }
    this.combined_main_results.registrations.sort(
      (a, b) => ((b.score ?? b.percentage ?? 0) - (a.score ?? a.percentage ?? 0))
    );
    this.combined_main_results.registrations.forEach((r, i) => { r.index = i + 1; });
    // New array reference so the results body sees the merge (it was pushed/sorted in place).
    this.combined_main_results.registrations = [...this.combined_main_results.registrations];
    this.pending_merge = false; // seamless auto-merge: no manual "refresh to merge" prompt
    this.combined_results_change.emit(this.combined_main_results);
  }

  // Merge a duplicate `incoming` into the already-shown `existing` row without losing useful
  // information. Listing data (price, seller, breakdown, finance) is adopted when `existing` has
  // no real listing, and the cheaper real listing wins when both have one (matching the
  // "starting from" display); otherwise individually-missing fields are filled. Ranking fields are
  // overwritten only by an authoritative (Plato) source so a dealer score never demotes a row.
  private mergeRegistration(existing: Registration, incoming: Registration, incomingAuthoritative: boolean): void {
    const hasListing = (r: Registration) => r && r.seller != null && r.price != null && r.price > 0;
    const adoptListing = () => {
      existing.price = incoming.price;
      existing.seller = incoming.seller;
      existing.priceBreakdown = incoming.priceBreakdown;
      existing.startingFrom = incoming.startingFrom;
      existing.max_price = incoming.max_price;
      existing.finance = incoming.finance;
      existing.financeDetails = incoming.financeDetails;
      existing.plateOwner = incoming.plateOwner;
      existing.plate_owner = incoming.plate_owner;
    };
    if (!hasListing(existing) && hasListing(incoming)) {
      adoptListing();
    } else if (hasListing(existing) && hasListing(incoming) && incoming.price < existing.price) {
      adoptListing(); // cheaper real listing wins (starting-from semantics)
    } else {
      if (existing.seller == null && incoming.seller != null) existing.seller = incoming.seller;
      if ((existing.price == null || existing.price < 0) && incoming.price != null && incoming.price >= 0) existing.price = incoming.price;
      if (existing.priceBreakdown == null && incoming.priceBreakdown != null) existing.priceBreakdown = incoming.priceBreakdown;
      if (existing.financeDetails == null && incoming.financeDetails != null) { existing.finance = incoming.finance; existing.financeDetails = incoming.financeDetails; }
    }
    if (existing.registered == null && incoming.registered != null) existing.registered = incoming.registered;
    if (existing.date_added == null && incoming.date_added != null) { existing.date_added = incoming.date_added; existing.date = incoming.date; }
    existing.meta_data = { ...(incoming.meta_data || {}), ...(existing.meta_data || {}) };
    existing.available = existing.price != null && existing.price >= 0;
    if (incomingAuthoritative) {
      existing.score = incoming.score;
      existing.percentage = incoming.percentage;
      existing.stars = incoming.stars;
      existing.fullStars = incoming.fullStars;
      existing.halfStar = incoming.halfStar;
      existing.emptyStars = incoming.emptyStars;
      if (incoming.term != null) existing.term = incoming.term;
    }
  }

  // Seamless progressive merge means results fold in automatically, so the manual "refresh to
  // merge" control is no longer surfaced (pending_merge stays false). Retained as a safe re-emit
  // in case the button is ever shown by configuration.
  public MergeResults(): void {
    this.pending_merge = false;
    if (this.combined_main_results != null) this.combined_results_change.emit(this.combined_main_results);
  }

  private extractNewlyListedFromResults(): void {
    //var auctionPlates: Registration[] = [];
    var newStylePlates: Registration[] = [];
    if (!this.results) return;
    [...this.combined_main_results.registrations].forEach((reg: Registration, i: number) => {
      if (reg.style == 'current' && reg.registration.substring(2, 4) == environment.latestRelease)
        newStylePlates.push(reg);
    });

    const tempSearchReq = new SearchReqResponse('newly_listed', [
      ...newStylePlates.slice(0, 2),
    ]);
    this.newlyListedResults = tempSearchReq;
    this.newlyListedResultChange.emit(this.newlyListedResults);
  }
}
